import { useEffect, useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { Phone, Menu, X, ChevronDown } from "lucide-react";
import { COMPANY, NAV_LINKS } from "../lib/constants";
import { Button } from "./ui/button";

export default function Header() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 left-0 w-full z-50 transition-all duration-300 ${
        scrolled
          ? "bg-white/95 backdrop-blur-md shadow-sm border-b border-gray-100"
          : "bg-white/80 backdrop-blur-sm"
      }`}
    >
      <div className="container-pad flex items-center justify-between py-2 md:py-3">
        <Link to="/" data-testid="header-logo-link" className="flex items-center gap-3">
          <img
            src={COMPANY.logo}
            alt="1st Home Care Services logo"
            className="h-16 md:h-20 w-auto object-contain"
            style={{ mixBlendMode: "multiply", filter: "saturate(1.15) contrast(1.05)" }}
            loading="eager"
          />
          <div className="leading-tight hidden sm:block">
            <div className="font-heading font-bold text-brand-blue-dark text-lg md:text-xl">
              1st Home Care
            </div>
            <div className="text-xs md:text-sm text-brand-amber-dark font-semibold">
              First-class care at your comfort zone
            </div>
          </div>
        </Link>

        <nav className="hidden lg:flex items-center gap-8">
          {NAV_LINKS.map((l) => {
            const slug = l.label.toLowerCase().replace(/\s+/g, "-");
            if (l.submenu) {
              return (
                <div
                  key={l.to}
                  className="relative group"
                  data-testid={`nav-dropdown-${slug}`}
                >
                  <NavLink
                    to={l.to}
                    data-testid={`nav-link-${slug}`}
                    className={({ isActive }) =>
                      `inline-flex items-center gap-1 text-sm font-semibold transition-colors ${
                        isActive
                          ? "text-brand-blue"
                          : "text-gray-700 hover:text-brand-blue"
                      }`
                    }
                  >
                    {l.label}
                    <ChevronDown className="w-3.5 h-3.5 transition-transform group-hover:rotate-180" />
                  </NavLink>
                  <div
                    data-testid={`nav-submenu-${slug}`}
                    className="absolute left-1/2 -translate-x-1/2 top-full pt-3 opacity-0 invisible group-hover:opacity-100 group-hover:visible group-focus-within:opacity-100 group-focus-within:visible transition-all duration-200 z-50"
                  >
                    <div className="min-w-[180px] bg-white rounded-xl shadow-xl border border-gray-100 py-2 overflow-hidden">
                      {l.submenu.map((s) => (
                        <NavLink
                          key={s.to}
                          to={s.to}
                          end
                          data-testid={`nav-submenu-link-${s.label.toLowerCase().replace(/\s+/g, "-")}`}
                          className={({ isActive }) =>
                            `block px-4 py-2.5 text-sm font-medium transition-colors ${
                              isActive
                                ? "bg-brand-blue/10 text-brand-blue"
                                : "text-gray-700 hover:bg-brand-gray hover:text-brand-blue-dark"
                            }`
                          }
                        >
                          {s.label}
                        </NavLink>
                      ))}
                    </div>
                  </div>
                </div>
              );
            }
            return (
              <NavLink
                key={l.to}
                to={l.to}
                data-testid={`nav-link-${slug}`}
                className={({ isActive }) =>
                  `text-sm font-semibold transition-colors ${
                    isActive
                      ? "text-brand-blue"
                      : "text-gray-700 hover:text-brand-blue"
                  }`
                }
                end={l.to === "/"}
              >
                {l.label}
              </NavLink>
            );
          })}
        </nav>

        <div className="flex items-center gap-3">
          <a
            href={`tel:${COMPANY.phoneTel}`}
            data-testid="header-call-now"
            className="hidden md:inline-flex items-center gap-2 bg-brand-blue text-white font-semibold px-5 py-2.5 rounded-full hover:bg-brand-blue-dark transition-all shadow-sm hover:shadow-md"
          >
            <Phone className="w-4 h-4" />
            {COMPANY.phone}
          </a>

          <button
            data-testid="mobile-menu-toggle"
            className="lg:hidden p-2 rounded-md text-brand-blue-dark"
            onClick={() => setOpen((o) => !o)}
            aria-label="Toggle navigation"
          >
            {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {/* Mobile drawer */}
      {open && (
        <div
          data-testid="mobile-menu"
          className="lg:hidden bg-white border-t border-gray-100 shadow-lg animate-fade-in"
        >
          <div className="container-pad py-4 flex flex-col gap-1">
            {NAV_LINKS.map((l) => {
              const slug = l.label.toLowerCase().replace(/\s+/g, "-");
              return (
                <div key={l.to} className="flex flex-col">
                  <NavLink
                    to={l.to}
                    onClick={() => setOpen(false)}
                    data-testid={`mobile-nav-${slug}`}
                    className={({ isActive }) =>
                      `px-4 py-3 rounded-lg text-base font-semibold ${
                        isActive
                          ? "bg-brand-blue/10 text-brand-blue"
                          : "text-gray-800 hover:bg-gray-50"
                      }`
                    }
                    end={l.to === "/"}
                  >
                    {l.label}
                  </NavLink>
                  {l.submenu && (
                    <div className="ml-4 mt-1 mb-1 border-l-2 border-brand-amber/40 pl-3 flex flex-col gap-0.5">
                      {l.submenu
                        .filter((s) => s.to !== l.to)
                        .map((s) => (
                          <NavLink
                            key={s.to}
                            to={s.to}
                            end
                            onClick={() => setOpen(false)}
                            data-testid={`mobile-nav-submenu-${s.label.toLowerCase().replace(/\s+/g, "-")}`}
                            className={({ isActive }) =>
                              `px-3 py-2 rounded-md text-sm font-medium ${
                                isActive
                                  ? "bg-brand-blue/10 text-brand-blue"
                                  : "text-gray-700 hover:bg-gray-50"
                              }`
                            }
                          >
                            {s.label}
                          </NavLink>
                        ))}
                    </div>
                  )}
                </div>
              );
            })}
            <a
              href={`tel:${COMPANY.phoneTel}`}
              data-testid="mobile-call-now"
              className="mt-3 inline-flex items-center justify-center gap-2 bg-brand-amber text-white font-semibold px-5 py-3 rounded-full"
            >
              <Phone className="w-4 h-4" /> Call {COMPANY.phone}
            </a>
          </div>
        </div>
      )}
    </header>
  );
}
